import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InputAdornment, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { ReactComponent as HeartIcon } from '../../../../assets/icons/user-plan/heart.svg';
import AppButton from '../../../../components/designe/buttons/app-button/AppButton';
import { errorToastMessage } from '../../../../components/layout/toast-root/toast';
import { useCreateDonationIntent } from '../../../../redux/api/paymentsApi';
import {
  DONATION_MAX_AMOUNT_MINOR,
  DONATION_MIN_AMOUNT_MINOR,
  PRESET_DONATION_DOLLARS,
  useStyles,
} from './DonationBlock.styles';

export interface IDonationBlockProps {
  onPaymentIntentReady: (clientSecret: string) => void;
}

function parseDollarsToMinor(raw: string): number | null {
  const normalized = raw.replace(',', '.').trim();
  if (normalized === '') {
    return null;
  }
  const num = Number.parseFloat(normalized);
  if (Number.isNaN(num) || num <= 0) {
    return null;
  }
  const minor = Math.round(num * 100);
  if (minor < DONATION_MIN_AMOUNT_MINOR || minor > DONATION_MAX_AMOUNT_MINOR) {
    return null;
  }
  return minor;
}

export default function DonationBlock(props: IDonationBlockProps) {
  const { onPaymentIntentReady } = props;
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const [amountInput, setAmountInput] = useState('5');
  const [createDonationIntent, { isLoading }] = useCreateDonationIntent();

  const amountMinorPreview = useMemo(
    () => parseDollarsToMinor(amountInput),
    [amountInput],
  );

  function applyPreset(dollars: number) {
    setAmountInput(String(dollars));
  }

  function onSupportClick() {
    const minor = parseDollarsToMinor(amountInput);
    if (minor == null) {
      errorToastMessage(t('subscriptionPlan.donationAmountInvalid'));
      return;
    }
    createDonationIntent({ amountMinor: minor })
      .unwrap()
      .then((data) => {
        if (data.clientSecret) {
          onPaymentIntentReady(data.clientSecret);
        } else {
          errorToastMessage(t('subscriptionPlan.donationNoClientSecret'));
        }
      })
      .catch((error: unknown) => {
        const status = (error as { status?: number })?.status;
        if (status === 403) {
          errorToastMessage(t('subscriptionPlan.donationFeatureDisabled'));
          return;
        }
        if (status === 400) {
          errorToastMessage(t('subscriptionPlan.donationAmountInvalid'));
          return;
        }
        errorToastMessage(t('subscriptionPlan.donationIntentError'));
      });
  }

  return (
    <div className={classes.card}>
      <div className={classes.headerRow}>
        <div className={classes.iconWrap} aria-hidden>
          <HeartIcon className={classes.iconSvg} />
        </div>
        <div>
          <Typography variant="h2" component="h2" className={classes.title}>
            {t('subscriptionPlan.donationTitle')}
          </Typography>
          <Typography variant="text-md" className={classes.subtitle}>
            {t('subscriptionPlan.donationSubtitle')}
          </Typography>
        </div>
      </div>

      <div className={classes.presetGrid}>
        {PRESET_DONATION_DOLLARS.map((d) => (
          <AppButton
            key={d}
            type="button"
            variant="contained"
            color="secondary"
            className={classes.presetButton}
            disabled={isLoading}
            onClick={() => applyPreset(d)}>
            {d}$
          </AppButton>
        ))}
      </div>

      <div className={classes.amountRow}>
        <TextField
          className={classes.amountField}
          value={amountInput}
          onChange={(e) => setAmountInput(e.target.value)}
          disabled={isLoading}
          placeholder={t('subscriptionPlan.donationAmountPlaceholder')}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          inputProps={{ inputMode: 'decimal' }}
        />
        <AppButton
          type="button"
          variant="contained"
          color="primary"
          className={classes.supportButton}
          loading={isLoading}
          disabled={isLoading || amountMinorPreview == null}
          onClick={onSupportClick}>
          {t('subscriptionPlan.donationSupportButton')}
        </AppButton>
      </div>
    </div>
  );
}
